/**
 * Cuentas de la regla de la vista previa del editor de Formatos (pedido de
 * Andrey, 2 oct 2026: «una regla tipo Word / Google Docs»). Todo en mm desde
 * el borde izquierdo o superior de la HOJA, que es donde la regla pone su
 * cero: así el número bajo el marcador izquierdo es el margen izquierdo y la
 * escala no se corre al arrastrar.
 *
 * Sin encuadernación (pedido de Andrey, 3 oct 2026): el editor ya no la
 * muestra. Una plantilla que la traiga la suma al izquierdo al cargarse
 * (`plegarEncuadernacion`, en types.ts), así que aquí no cuenta.
 *
 * Funciones puras, sin DOM, para poder probar la conversión píxel→margen.
 */

export type Unidad = 'cm' | 'in';
export type LadoMargen = 'marginLeftMm' | 'marginRightMm' | 'marginTopMm' | 'marginBottomMm';

export const MM_POR_UNIDAD: Record<Unidad, number> = { cm: 10, in: 25.4 };
/** Paso del arrastre y de las flechas: 0,1 cm o 0,05 in (el `step` de los inputs). */
export const PASO_UNIDAD: Record<Unidad, number> = { cm: 0.1, in: 0.05 };

/*
 * Límites de Word (decisión de Andrey, 2 oct 2026: «los márgenes como
 * Word»). Son los MISMOS números que `validar()` de processing-ms
 * (format-templates.service.ts y common/posicion-banda.ts): si el editor
 * deja guardar algo, el backend lo acepta.
 */

/** Tamaño de cada hoja en mm, en vertical (las de Word: A4 11906×16838 tw, Carta 12240×15840, Oficio 12240×20160). */
export const HOJA_MM: Record<'a4' | 'letter' | 'legal', { ancho: number; alto: number }> = {
  a4: { ancho: 210, alto: 297 },
  letter: { ancho: 215.9, alto: 279.4 },
  legal: { ancho: 215.9, alto: 355.6 },
};

/** La hoja de la plantilla con la orientación ya aplicada (horizontal = ancho y alto cambiados). */
export function hojaMm(pageFormat?: string | null, orientacion?: string | null): { ancho: number; alto: number } {
  const base = HOJA_MM[(pageFormat ?? 'a4') as keyof typeof HOJA_MM] ?? HOJA_MM.a4;
  return orientacion === 'landscape' ? { ancho: base.alto, alto: base.ancho } : { ...base };
}

/** Área de texto mínima, de ancho y de alto: la columna más estrecha que admite Word (0,5"). */
export const AREA_MIN_MM = 12.7;

/** Distancia por defecto del encabezado y del pie al borde: la de Word (1,25 cm). */
export const DISTANCIA_BANDA_MM = 12.5;

/** Alto de un renglón de la letra de la plantilla, en mm. */
export const renglonMm = (fontSizePt?: number | null, lineHeight?: number | null): number =>
  ((fontSizePt ?? 12) * (lineHeight ?? 1.5) * 25.4) / 72;

/** Alto mínimo del área de texto: 12,7 mm o un renglón, lo que sea mayor. */
export const altoMinimoMm = (fontSizePt?: number | null, lineHeight?: number | null): number =>
  Math.max(AREA_MIN_MM, renglonMm(fontSizePt, lineHeight));

export interface GeometriaHoja {
  anchoMm: number;
  altoMm: number;
  sup: number;
  inf: number;
  izq: number;
  der: number;
  /** Alto mínimo del área de texto (`altoMinimoMm`); sin él, 12,7 mm. */
  altoMinMm?: number;
}

/** Recorta a la décima de mm hacia abajo, sin bajar de 0 (el `tope` de `validar()`). */
const tope = (mm: number): number => Math.max(0, Math.floor(mm * 10 + 1e-6) / 10);
/** Resta de mm sin el ruido de coma flotante (197,3 − 100 − 97,3 da 1,4e-14) y sin bajar de 0. */
const resto = (mm: number): number => Math.max(0, Math.round(mm * 1e6) / 1e6);

/**
 * Lo que pueden sumar los márgenes en la hoja: superior + inferior hasta el
 * alto menos el área mínima, e izquierdo + derecho hasta el ancho menos
 * 12,7 mm. `maxVertical` es también el tope de las distancias
 * del encabezado y del pie.
 */
export function limitesHoja(g: Pick<GeometriaHoja, 'anchoMm' | 'altoMm' | 'altoMinMm'>): { maxVertical: number; maxHorizontal: number } {
  return {
    maxVertical: tope(g.altoMm - (g.altoMinMm ?? AREA_MIN_MM)),
    maxHorizontal: tope(g.anchoMm - AREA_MIN_MM),
  };
}

/**
 * Valor más alto que puede tomar el lado cuando «Iguales» lo pone en todos
 * los `libres` a la vez: los que fija la norma conservan el suyo.
 */
export function maxIguales(g: GeometriaHoja, libres: readonly LadoMargen[]): number {
  const { maxVertical, maxHorizontal } = limitesHoja(g);
  const reparto = (lados: LadoMargen[], max: number) => {
    const n = lados.filter((l) => libres.includes(l)).length;
    const ocupado = lados.filter((l) => !libres.includes(l)).reduce((s, l) => s + margenMm(l, g), 0);
    return n ? (max - ocupado) / n : Infinity;
  };
  const v = reparto(['marginTopMm', 'marginBottomMm'], maxVertical);
  const h = reparto(['marginLeftMm', 'marginRightMm'], maxHorizontal);
  const max = Math.min(v, h);
  return Number.isFinite(max) ? tope(max) : 0;
}

/**
 * Un máximo en mm pasado a la unidad del campo, a la centésima hacia abajo:
 * lo que se teclee hasta ahí, guardado a la décima de mm, nunca lo supera.
 */
export const maxEnUnidad = (mm: number, unidad: Unidad): number =>
  Math.max(0, Math.floor((mm / MM_POR_UNIDAD[unidad]) * 100 + 1e-6) / 100);

/**
 * Dónde va el encabezado (o el pie) y dónde empieza el texto: copia de
 * `posicionBanda` de processing-ms (common/posicion-banda.ts), la regla del
 * Word y del PDF. La banda va a una distancia fija del borde y el texto
 * empieza en el mayor de dos valores: el margen, o la distancia más el alto
 * real de la banda. Sin banda (alto 0), el texto empieza en el margen.
 */
export interface PosicionBanda {
  distanciaMm: number;
  inicioTextoMm: number;
}

/** Una distancia guardada o recibida, en mm válidos: lo que no sea un número ≥ 0 cae a la de Word. */
export function distanciaBandaMm(v: unknown): number {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN;
  return Number.isFinite(n) && n >= 0 ? n : DISTANCIA_BANDA_MM;
}

export function posicionBanda(margenMm: number, distanciaMm: number, altoBandaMm: number): PosicionBanda {
  const margen = Number.isFinite(margenMm) && margenMm > 0 ? margenMm : 0;
  const distancia = distanciaBandaMm(distanciaMm);
  const alto = Number.isFinite(altoBandaMm) && altoBandaMm > 0 ? altoBandaMm : 0;
  return { distanciaMm: distancia, inicioTextoMm: alto > 0 ? Math.max(margen, distancia + alto) : margen };
}

/**
 * ¿La banda empuja el texto? Con un cuarto de mm de holgura: el alto medido
 * trae décimas de píxel y un empujón que no se ve no merece aviso.
 */
export const empujaTexto = (margenMm: number, inicioTextoMm: number): boolean => inicioTextoMm - margenMm > 0.25;

export const unidadDe = (u: string | null | undefined): Unidad => (u === 'in' ? 'in' : 'cm');

/**
 * Unidad → mm tal como lo guarda el formulario (inputs y regla). A la décima
 * de mm, que es lo que admite `validar()` del backend: con mm enteros, 2 in
 * se guardaban como 51 mm y volvían como «2,01 in».
 */
export const mmDeUnidad = (n: number, unidad: Unidad): number => Math.round(n * MM_POR_UNIDAD[unidad] * 10) / 10;

/** mm → unidad con dos decimales: lo que muestra el input. */
export const unidadDeMm = (mm: number, unidad: Unidad): number => Math.round((mm / MM_POR_UNIDAD[unidad]) * 100) / 100;

/**
 * Número escrito en un campo, con punto o con coma decimal («2.75», «2,75»);
 * null mientras no sea un número completo.
 */
export function leerNumero(texto: string): number | null {
  const s = texto.trim().replace(',', '.');
  if (!/^(\d+(\.\d*)?|\.\d+)$/.test(s)) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

export const esHorizontal = (lado: LadoMargen): boolean => lado === 'marginLeftMm' || lado === 'marginRightMm';

export function margenMm(lado: LadoMargen, g: GeometriaHoja): number {
  if (lado === 'marginLeftMm') return g.izq;
  if (lado === 'marginRightMm') return g.der;
  if (lado === 'marginTopMm') return g.sup;
  return g.inf;
}

/** Dónde cae el marcador de un lado, en mm desde el borde izquierdo o superior de la hoja. */
export function posicionMarcador(lado: LadoMargen, g: GeometriaHoja): number {
  if (lado === 'marginLeftMm') return g.izq;
  if (lado === 'marginRightMm') return g.anchoMm - g.der;
  if (lado === 'marginTopMm') return g.sup;
  return g.altoMm - g.inf;
}

/** Inversa de `posicionMarcador`: el margen que deja el marcador en `posMm`. */
export function margenDesdePosicion(lado: LadoMargen, posMm: number, g: GeometriaHoja): number {
  if (lado === 'marginLeftMm') return posMm;
  if (lado === 'marginRightMm') return g.anchoMm - posMm;
  if (lado === 'marginTopMm') return posMm;
  return g.altoMm - posMm;
}

/**
 * Rango del margen de un lado, como Word: desde 0 hasta lo que deja el lado
 * opuesto sin bajar del área de texto mínima.
 */
export function rangoMargen(lado: LadoMargen, g: GeometriaHoja): { min: number; max: number } {
  const { maxVertical, maxHorizontal } = limitesHoja(g);
  const max = lado === 'marginLeftMm' ? maxHorizontal - g.der
    : lado === 'marginRightMm' ? maxHorizontal - g.izq
    : lado === 'marginTopMm' ? maxVertical - g.inf
    : maxVertical - g.sup;
  return { min: 0, max: resto(max) };
}

/**
 * Margen pedido (mm, puede venir con decimales del puntero) → valor EN LA
 * UNIDAD, redondeado al paso y dentro del rango. Es lo que se entrega al
 * mismo setter de los inputs.
 */
export function ajustarMargen(mm: number, lado: LadoMargen, g: GeometriaHoja, unidad: Unidad): number {
  const k = MM_POR_UNIDAD[unidad];
  const paso = PASO_UNIDAD[unidad];
  const { min, max } = rangoMargen(lado, g);
  const lo = Math.ceil(min / k / paso - 1e-9) * paso;
  const hi = Math.floor(max / k / paso + 1e-9) * paso;
  const n = Math.round(mm / k / paso) * paso;
  // toFixed quita el ruido de coma flotante (0.30000000000000004).
  return Number(Math.min(hi, Math.max(lo, n)).toFixed(2));
}

/** Píxel del puntero → mm sobre la regla (0 en el borde de la hoja). */
export function mmDesdePuntero(clientPx: number, inicioPx: number, largoPx: number, largoMm: number): number {
  if (!(largoPx > 0)) return 0;
  return ((clientPx - inicioPx) / largoPx) * largoMm;
}

/**
 * Cuánto cambia el VALOR del margen con una flecha (+1, −1 o 0). En el eje
 * de su regla la flecha mueve el marcador hacia donde apunta (→ en el derecho
 * achica el margen); en el otro eje, ↑/→ suben el valor como en un slider.
 */
export function deltaTecla(key: string, lado: LadoMargen): -1 | 0 | 1 {
  if (esHorizontal(lado)) {
    const haciaDerecha = lado === 'marginLeftMm' ? 1 : -1;
    if (key === 'ArrowRight') return haciaDerecha;
    if (key === 'ArrowLeft') return haciaDerecha === 1 ? -1 : 1;
    if (key === 'ArrowUp') return 1;
    if (key === 'ArrowDown') return -1;
    return 0;
  }
  const haciaAbajo = lado === 'marginTopMm' ? 1 : -1;
  if (key === 'ArrowDown') return haciaAbajo;
  if (key === 'ArrowUp') return haciaAbajo === 1 ? -1 : 1;
  if (key === 'ArrowRight') return 1;
  if (key === 'ArrowLeft') return -1;
  return 0;
}

/** Margen tras una flecha, ya ajustado (en la unidad). */
export function margenTrasTecla(lado: LadoMargen, delta: -1 | 1, g: GeometriaHoja, unidad: Unidad): number {
  const pasoMm = PASO_UNIDAD[unidad] * MM_POR_UNIDAD[unidad];
  return ajustarMargen(margenMm(lado, g) + delta * pasoMm, lado, g, unidad);
}

export interface MarcaRegla {
  mm: number;
  /** 0 = unidad entera (lleva número), 1 = media, 2 = cuarto, 3 = octavo (solo pulgadas). */
  nivel: 0 | 1 | 2 | 3;
  numero?: number;
}

/**
 * Marcas de la regla: en cm cada 0,25 (número en cada cm, marca media en
 * 0,5); en pulgadas cada 1/8 (número en cada pulgada, marcas en 1/2, 1/4 y
 * 1/8). El cero y los números pegados al borde final no se rotulan.
 * `cadaNumero` = 2 rotula solo las unidades pares (hojas apaisadas en cm,
 * donde 1 cm mide ~13 px y los números de dos cifras se tocan).
 */
export function marcasRegla(largoMm: number, unidad: Unidad, cadaNumero: 1 | 2 = 1): MarcaRegla[] {
  const divisiones = unidad === 'cm' ? 4 : 8;
  const paso = MM_POR_UNIDAD[unidad] / divisiones;
  const marcas: MarcaRegla[] = [];
  for (let i = 1; i * paso <= largoMm + 1e-6; i++) {
    const mm = i * paso;
    if (i % divisiones === 0) {
      const numero = i / divisiones;
      marcas.push(largoMm - mm >= 2 && numero % cadaNumero === 0 ? { mm, nivel: 0, numero } : { mm, nivel: 0 });
    } else if (i % (divisiones / 2) === 0) marcas.push({ mm, nivel: 1 });
    else if (i % (divisiones / 4) === 0) marcas.push({ mm, nivel: 2 });
    else marcas.push({ mm, nivel: 3 });
  }
  return marcas;
}
